import { useMemo } from 'react';
import { useGetLanguageSettings } from './queries/languageSettings';

/**
 * Custom hook for language selector logic
 * Provides consistent language selection functionality across all components
 * 
 * @param {Object} fallbackData - Fallback data from props (e.g., area, location, tag)
 * @returns {Object} Language selector data and utilities
 */
export function useLanguageSelector(fallbackData = {}) {
  const { data: languageSettings } = useGetLanguageSettings();
  
  return useMemo(() => {
    // Extract enabled language codes from the enabled_languages object
    const enabledLanguageCodes = languageSettings?.localization?.enabled_languages 
      ? Object.keys(languageSettings.localization.enabled_languages)
      : [];
    
    // Always include the default locale, then add enabled languages
    const defaultLocale = languageSettings?.localization?.default_locale || fallbackData?.current_locale || "en";
    let availableLocaleCodes = [defaultLocale];
    
    if (languageSettings?.localization?.enabled && enabledLanguageCodes.length > 0) {
      // Add enabled languages and remove duplicates
      availableLocaleCodes = [...new Set([defaultLocale, ...enabledLanguageCodes])];
    } else if (fallbackData?.available_locales) {
      // Fallback to props data for backwards compatibility
      availableLocaleCodes = fallbackData.available_locales.map(locale => 
        typeof locale === 'object' ? locale.code : locale
      );
    }
    
    // Map to locale objects from available_locales if possible
    const availableLocales = availableLocaleCodes.map(code => {
      // Try to find full locale info from languageSettings
      const fullLocale = languageSettings?.available_locales?.find(locale => locale.code === code);
      if (fullLocale) {
        return fullLocale;
      }
      // Fallback to code only
      return { code, name: code.toUpperCase() };
    });
    
    const currentLocale = defaultLocale;
    const isLocalizationEnabled = languageSettings?.localization?.enabled || fallbackData?.localization_enabled || false;
    
    // Helper function to get locale display name
    const getLocaleDisplayName = (localeCode) => {
      // Try to find in available_locales from language settings first
      if (languageSettings?.available_locales) {
        const locale = languageSettings.available_locales.find(l => l.code === localeCode);
        if (locale) return locale.name;
      }
      
      // Fallback to availableLocales array (for backwards compatibility)
      if (Array.isArray(availableLocales)) {
        const locale = availableLocales.find(l => l.code === localeCode);
        if (locale) return locale.name;
      }
      return localeCode.toUpperCase();
    };
    
    // Generate selector options
    const localeOptions = availableLocales.map(locale => ({
      label: locale.name,
      value: locale.code
    }));
    
    return {
      languageSettings,
      availableLocales,
      currentLocale,
      defaultLocale,
      isLocalizationEnabled,
      localeOptions,
      getLocaleDisplayName,
      enabledLanguageCodes
    };
  }, [languageSettings, fallbackData]);
}

export default useLanguageSelector; 